import { Flange } from './flange';
import { Hole } from './hole';
import { Part, SVG_NS, ViewContext } from './part';
import { Rim } from './rim';
import { Spoke } from './spoke';
import { getState } from './state';
import { roundTo } from './utils';

export interface WheelProps {
  rimErd: number;
  rimNumHoles: number;
  rimDrillingOffsetDs: number;
  rimDrillingOffsetNds: number;
  rimFlipOffsetOrder: boolean;
  dsFlangePcd: number;
  ndsFlangePcd: number;
  dsFlangeOffset: number;
  ndsFlangeOffset: number;
  dsFlangeNumHoles: number;
  ndsFlangeNumHoles: number;
  hubRotation: number;
  dsNdsOffset: number;
  spokeHoleDiameter: number;
  spokeGroupingPrecision: number;
}

export class Wheel extends Part {
  rim: Rim;
  dsFlange: Flange;
  ndsFlange: Flange;
  spokes: Spoke[] = [];

  constructor(
    public id: string,
    private props: WheelProps
  ) {
    super();
    this.rim = this.generateRim();
    const [dsFlange, ndsFlange] = this.generateFlanges();
    this.dsFlange = dsFlange;
    this.ndsFlange = ndsFlange;
  }

  get children(): Part[] {
    return [this.rim, this.dsFlange, this.ndsFlange, ...this.spokes];
  }

  update(props: Partial<WheelProps>): void {
    this.props = {
      ...this.props,
      ...props,
    };
    this.rim.update({
      erd: this.props.rimErd,
      numHoles: this.props.rimNumHoles,
      holeDiameter: this.props.spokeHoleDiameter,
      drillingOffsetDs: this.props.rimDrillingOffsetDs,
      drillingOffsetNds: this.props.rimDrillingOffsetNds,
      flipOffsetOrder: this.props.rimFlipOffsetOrder,
    });
    this.dsFlange.update({
      pcd: this.props.dsFlangePcd,
      numHoles: this.props.dsFlangeNumHoles,
      rotation: this.props.hubRotation,
      spokeHoleDiameter: this.props.spokeHoleDiameter,
      centerOffset: this.props.dsFlangeOffset,
    });
    this.ndsFlange.update({
      pcd: this.props.ndsFlangePcd,
      numHoles: this.props.ndsFlangeNumHoles,
      rotation: this.props.hubRotation - this.props.dsNdsOffset,
      spokeHoleDiameter: this.props.spokeHoleDiameter,
      centerOffset: this.props.ndsFlangeOffset,
    });
    this.cleanupSpokes();
    this.groupSpokes();
  }

  get size(): number {
    return this.props.rimErd + 15 * 2;
  }

  private generateRim() {
    return new Rim('RIM', {
      erd: this.props.rimErd,
      thickness: 15,
      numHoles: this.props.rimNumHoles,
      holeDiameter: this.props.spokeHoleDiameter,
      drillingOffsetDs: this.props.rimDrillingOffsetDs,
      drillingOffsetNds: this.props.rimDrillingOffsetNds,
      flipOffsetOrder: this.props.rimFlipOffsetOrder,
    });
  }

  private generateFlanges() {
    const dsFlange = new Flange('FLANGE_DS', {
      pcd: this.props.dsFlangePcd,
      numHoles: this.props.dsFlangeNumHoles,
      side: 'ds',
      rotation: this.props.hubRotation,
      spokeHoleDiameter: this.props.spokeHoleDiameter,
      centerOffset: this.props.dsFlangeOffset,
    });
    const ndsFlange = new Flange('FLANGE_NDS', {
      pcd: this.props.ndsFlangePcd,
      numHoles: this.props.ndsFlangeNumHoles,
      side: 'nds',
      rotation: this.props.hubRotation - this.props.dsNdsOffset,
      spokeHoleDiameter: this.props.spokeHoleDiameter,
      centerOffset: this.props.ndsFlangeOffset,
    });
    return [dsFlange, ndsFlange];
  }

  private reorderChildren(viewCtx: ViewContext): Part[] {
    const dsSpokes = this.spokes.filter((spoke) => spoke.props.flangeHole.location === 'flange-ds');
    const ndsSpokes = this.spokes.filter(
      (spoke) => spoke.props.flangeHole.location === 'flange-nds'
    );
    if (viewCtx.viewSide === 'ds') {
      return [this.rim, this.ndsFlange, ...ndsSpokes, this.dsFlange, ...dsSpokes];
    } else {
      return [this.rim, this.dsFlange, ...dsSpokes, this.ndsFlange, ...ndsSpokes];
    }
  }

  addSpoke(holeOne: Hole, holeTwo: Hole): boolean {
    if (holeOne.isOnFlange && holeTwo.isOnRim) {
      const flangeHole = holeOne;
      const rimHole = holeTwo;
      const spoke = new Spoke(`${flangeHole.id}${rimHole.id}`, {
        flangeHole,
        rimHole,
      });
      this.spokes.push(spoke);
      getState().select(spoke.id);
      this.groupSpokes();
      return true;
    } else if (holeOne.isOnRim && holeTwo.isOnFlange) {
      const flangeHole = holeTwo;
      const rimHole = holeOne;
      const spoke = new Spoke(`${flangeHole.id}${rimHole.id}`, {
        flangeHole,
        rimHole,
      });
      this.spokes.push(spoke);
      getState().select(spoke.id);
      this.groupSpokes();
      return true;
    }
    return false;
  }

  removeSpoke(id: string) {
    this.spokes = this.spokes.filter((spoke) => spoke.id !== id);
    this.groupSpokes();
  }

  removeAllSpokes() {
    this.spokes = [];
  }

  cleanupSpokes() {
    // Remove orphaned spokes
    this.spokes = this.spokes.filter((spoke) => {
      return this.find(spoke.props.rimHole.id) && this.find(spoke.props.flangeHole.id);
    });
  }

  groupSpokes() {
    const roundPrecision = this.props.spokeGroupingPrecision;
    const spokeLengths = Array.from(
      new Set(this.spokes.map((spoke) => roundTo(spoke.length, roundPrecision)))
    );
    spokeLengths.sort();
    for (let i = 0; i < this.spokes.length; i++) {
      const l = roundTo(this.spokes[i].length, roundPrecision);
      const group = String.fromCharCode(65 + spokeLengths.indexOf(l));
      this.spokes[i].group = group;
    }
  }

  getSpokeGroups(): [string, number, number][] {
    const groups: Record<string, [number, number]> = {};
    for (const spoke of this.spokes) {
      if (!spoke.group) continue;
      if (spoke.group in groups) {
        groups[spoke.group][1] += 1;
      } else {
        groups[spoke.group] = [roundTo(spoke.length, this.props.spokeGroupingPrecision), 1];
      }
    }
    return Object.entries(groups)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([group, v]) => [group, ...v]);
  }

  get spokeDefs(): string {
    return this.spokes
      .map((spoke) => `${spoke.props.rimHole.id}${spoke.props.flangeHole.id}`)
      .join('');
  }

  generateSpokesFromDefs(definitions: string): void {
    const rgx = /(?<rim>R\d+)(?<flange>(N|S)\d+)/gm;
    const matches = Array.from(definitions.matchAll(rgx));
    const takenHoleIds: string[] = [];
    this.removeAllSpokes();
    for (const match of matches) {
      const rimHoleId = match.groups?.rim;
      const flangeHoleId = match.groups?.flange;
      if (!rimHoleId || !flangeHoleId) continue;
      if (rimHoleId in takenHoleIds || flangeHoleId in takenHoleIds) continue;
      const rimHole = this.find(rimHoleId);
      const flangeHole = this.find(flangeHoleId);
      if (!rimHole || !flangeHole) continue;
      this.addSpoke(rimHole as Hole, flangeHole as Hole);
    }
    getState().select(undefined);
  }

  hasExtremeAngles(threshold: number): boolean {
    return this.spokes.some((spoke) => spoke.hasExtremeAngles(threshold));
  }

  render(viewCtx: ViewContext): SVGElement {
    const group = document.createElementNS(SVG_NS, 'g');
    group.classList.add('wheel');
    const reorderedChildren = this.reorderChildren(viewCtx);
    reorderedChildren.forEach((child) => {
      group.appendChild(child.render(viewCtx));
    });
    return group;
  }
}
